/* Config for the short submission workflows (Award, Certification, Event, Media).
   Each flow renders through the generic SubmitShort wizard.
   Question sets follow the spec in the Feb-2026 capture-logic brief. */

export const ENTRY_TYPES = {
  project: { key: "project", label: "Project", short: "Detailed case-study workflow", icon: "FileText" },
  award: { key: "award", label: "Award", short: "Capture awards & shortlists", icon: "Trophy" },
  certification: { key: "certification", label: "Certification / Accreditation", short: "ISO and accreditations", icon: "ShieldCheck" },
  event: { key: "event", label: "Event", short: "Conferences, dinners, exhibitions", icon: "Calendar" },
  social: { key: "social", label: "Social Value / Community", short: "Charity, sponsorships, volunteering", icon: "Heart" },
  media: { key: "media", label: "Media Coverage & News", short: "Press, articles, social", icon: "Newspaper" },
  photos_first: { key: "photos_first", label: "Photos First", short: "Fast on-site capture", icon: "Camera" },
};

const startStep = (nameField) => ({
  title: "Get started",
  hint: "Give it a name first — that's how it will appear in the library — then confirm who's submitting.",
  fields: [
    { ...nameField, required: true },
    { key: "submitter_name", label: "Your name", required: true, placeholder: "e.g. Scott Williams" },
    { key: "submitter_email", label: "Your email address", required: true, type: "email", placeholder: "you@randcwilliams.co.uk" },
  ],
});

const FILES_STEP = (label = "Files", note = "Upload supporting documents, screenshots, photos.") => ({
  title: label,
  hint: note,
  upload: true,
  fields: [
    { key: "type_notes", label: "Notes", type: "textarea", placeholder: "Anything we should know about the files?" },
  ],
});

/* ---------- Award ---------- */
export const AWARD_FLOW = {
  type: "award",
  label: "Award",
  emailLabel: "Award",
  steps: [
    startStep({ key: "type_data.award_name", label: "Award name", placeholder: "e.g. Highways Awards 2026" }),
    {
      title: "Award details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.organisation", label: "Organiser", placeholder: "e.g. CIHT" },
        { key: "type_data.category", label: "Category", placeholder: "e.g. Project Team of the Year" },
        { key: "type_data.year", label: "Year", placeholder: "e.g. 2026" },
        { key: "type_data.outcome", label: "Result", type: "select", options: ["Winner", "Highly Commended", "Finalist", "Shortlisted", "Submitted", "Pending"] },
        { key: "type_data.related_project", label: "Project, team or product involved", placeholder: "Project or team name if applicable" },
      ],
    },
    {
      title: "The story",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.what_recognised", label: "What was recognised?", type: "textarea", placeholder: "The scheme, the team, the innovation, the outcomes…" },
        { key: "type_data.why_entered", label: "Why did we enter?", type: "textarea", placeholder: "What made this worth entering?" },
        { key: "type_data.key_achievements", label: "Key achievements", type: "textarea", placeholder: "The stand-out things this project or team delivered." },
        { key: "type_data.stats_results", label: "Statistics / results", type: "textarea", placeholder: "Numbers, KPIs, before/after figures, savings, tonnes, %…" },
        { key: "type_data.team_members", label: "Team members involved", type: "textarea", placeholder: "Who did the work?" },
        { key: "type_data.judges_feedback", label: "Judges feedback (if available)", type: "textarea", placeholder: "Copy any quotes or comments from the judges here." },
      ],
    },
    {
      title: "Evidence & files",
      eyebrow: "Step 3",
      upload: true,
      fields: [
        { key: "type_data.outcome_notes", label: "Additional notes", type: "textarea", placeholder: "Certificates, photos, logos, links…" },
      ],
    },
  ],
};

/* ---------- Certification / Accreditation ---------- */
export const CERT_FLOW = {
  type: "certification",
  label: "Certification / Accreditation",
  emailLabel: "Certification",
  steps: [
    startStep({ key: "type_data.certification_name", label: "Certification name", placeholder: "e.g. ISO 14001" }),
    {
      title: "Certification details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.issuing_organisation", label: "Awarding organisation", placeholder: "e.g. BSI" },
        { key: "type_data.date_achieved", label: "Date achieved", type: "date" },
        { key: "type_data.renewal_date", label: "Expiry / renewal date", type: "date" },
      ],
    },
    {
      title: "What it proves",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.what_shows", label: "What does this certification prove?", type: "textarea" },
        { key: "type_data.why_important", label: "Why is it important?", type: "textarea" },
        { key: "type_data.supports", label: "Which services does it support?", type: "textarea", placeholder: "e.g. Highways, Structures, Drainage" },
        { key: "type_data.compliance_benefits", label: "Compliance benefits", type: "textarea", placeholder: "How does this help us meet standards / obligations?" },
        { key: "type_data.client_benefits", label: "Client benefits", type: "textarea", placeholder: "What does the client get out of us holding this?" },
      ],
    },
    FILES_STEP("Upload certificate", "Attach the certificate PDF and any supporting evidence."),
  ],
};

/* ---------- Event ---------- */
export const EVENT_FLOW = {
  type: "event",
  label: "Event",
  emailLabel: "Event",
  steps: [
    startStep({ key: "type_data.event_name", label: "Event name", placeholder: "e.g. Highways UK" }),
    {
      title: "Event details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.event_type", label: "Type", type: "select",
          options: ["Conference", "Exhibition", "Charity", "Team event", "Training", "Networking", "Awards Dinner", "Client Event", "Industry Event", "Other"] },
        { key: "type_data.location", label: "Location", placeholder: "e.g. NEC Birmingham" },
        { key: "type_data.date", label: "Date", type: "date" },
      ],
    },
    {
      title: "The story",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.who_attended", label: "Who attended?", type: "textarea", placeholder: "R&C people who represented us." },
        { key: "type_data.why_attended", label: "Why did we attend?", type: "textarea", placeholder: "What was the purpose / opportunity?" },
        { key: "type_data.key_moments", label: "Key moments", type: "textarea", placeholder: "Stand-out moments, talks, meetings, wins." },
        { key: "type_data.people_involved", label: "People involved", type: "textarea", placeholder: "Clients, partners, sub-contractors, sponsors." },
        { key: "type_data.outcomes", label: "Outcomes", type: "textarea", placeholder: "New contacts, ideas, business dev, learnings." },
      ],
    },
    {
      title: "Photos, video & links",
      eyebrow: "Step 3",
      upload: true,
      fields: [
        { key: "type_data.outcome_notes", label: "Anything else worth capturing?", type: "textarea" },
      ],
    },
  ],
};

/* ---------- Media Coverage & News ---------- */
export const MEDIA_FLOW = {
  type: "media",
  label: "Media Coverage & News",
  emailLabel: "Media Coverage & News",
  steps: [
    startStep({ key: "type_data.title", label: "Title", placeholder: "e.g. R&C Williams wins Leicester contract" }),
    {
      title: "Coverage details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.publication", label: "Publication / source", placeholder: "e.g. New Civil Engineer" },
        { key: "type_data.date", label: "Date", type: "date" },
        { key: "type_data.url", label: "Link", placeholder: "https://" },
        { key: "type_data.related_project", label: "Related project (optional)" },
        { key: "type_data.coverage_type", label: "Coverage type", type: "select",
          options: ["Press Release", "Company News", "Project News", "Contract Win", "Award News", "Industry Article", "Magazine Feature", "Website Article", "Podcast Feature", "Video Feature", "Client Feature", "Social Media Coverage", "Other"] },
      ],
    },
    {
      title: "The story",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.about", label: "What was covered?", type: "textarea" },
        { key: "type_data.why_important", label: "Why is it important?", type: "textarea" },
        { key: "type_data.featured", label: "People / projects mentioned", type: "textarea" },
        { key: "type_data.key_messages", label: "Key messages", type: "textarea", placeholder: "Headlines and phrases we want to remember + reuse." },
      ],
    },
    FILES_STEP("Screenshots & files", "Upload screenshots, PDFs, images, video — or paste links in Notes."),
  ],
};

/* ---------- Social Value / Community ---------- */
export const SOCIAL_FLOW = {
  type: "social",
  label: "Social Value / Community",
  emailLabel: "Social Value",
  steps: [
    startStep({ key: "type_data.initiative_name", label: "Initiative name", placeholder: "e.g. Local U12s Football Club Sponsorship" }),
    {
      title: "Initiative details",
      eyebrow: "Step 1",
      fields: [
        { key: "type_data.initiative_type", label: "Type", type: "select",
          options: ["Sponsorship", "Charity fundraising", "Donation", "Volunteering", "Community event", "School / education", "Environmental", "Apprenticeships / training", "Other"] },
        { key: "type_data.who_supported", label: "Who did we support?", placeholder: "e.g. Bromsgrove U12s FC, Air Ambulance" },
        { key: "type_data.location", label: "Location / community", placeholder: "e.g. Bromsgrove, Worcestershire" },
        { key: "type_data.date", label: "Date", type: "date" },
      ],
    },
    {
      title: "The story",
      eyebrow: "Step 2",
      fields: [
        { key: "type_data.why_involved", label: "Why did we get involved?", type: "textarea", placeholder: "The connection, the cause, who asked us…" },
        { key: "type_data.what_we_did", label: "What did we do?", type: "textarea", placeholder: "e.g. paid for the new kit, volunteered a day, donated materials…" },
        { key: "type_data.who_took_part", label: "Who from R&C took part?", type: "textarea", placeholder: "Names and roles of people involved." },
        { key: "type_data.impact", label: "The difference it made", type: "textarea", placeholder: "What it meant to the people or community helped." },
        { key: "type_data.amount_raised", label: "Amount raised / donated (if any)", placeholder: "e.g. £500 raised for Air Ambulance" },
      ],
    },
    FILES_STEP("Photos & files", "Photos of the day, the team, certificates, thank-you letters…"),
  ],
};

export const FLOWS = {
  award: AWARD_FLOW,
  certification: CERT_FLOW,
  event: EVENT_FLOW,
  media: MEDIA_FLOW,
  social: SOCIAL_FLOW,
};
